import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
} from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
} from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    if (productCard) {
      return productCard.productPageUrl;
    }

    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest('product-hotspot-component')
      );

    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card.
   *
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  /**
   * Updates quick-add button state when a product-card variant is selected.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    ) {
      return;
    }

    if (this.dataset.usesSellingPlans === 'true') return;

    /*
     * Only switch Choose/Add when both buttons exist.
     */
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount =
      this.dataset.productOptionsCount;

    const quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  /**
   * Clears cached Quick View content when the cart changes.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker inside the Quick View modal.
   *
   * @param {Element} newHtml
   */
  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector(
          'variant-picker'
        )
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles the Quick View button click.
   *
   * The clicked button displays Loading... while the product page
   * content is fetched and prepared.
   *
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    /*
     * Fabric uses delegated component events, so currentTarget is not
     * guaranteed to be the actual button. Find it from event.target.
     */
    const clickedElement =
      event.target instanceof Element
        ? event.target
        : null;

    const targetButton =
      clickedElement?.closest(
        'button.quick-add__button--choose'
      );

    /*
     * Fall back to this component's Choose button when the click target
     * cannot be resolved directly.
     */
    const button =
      targetButton instanceof HTMLButtonElement &&
      this.contains(targetButton)
        ? targetButton
        : this.querySelector(
            'button.quick-add__button--choose'
          );

    if (!(button instanceof HTMLButtonElement)) {
      console.warn(
        '[quick-view] Quick View button could not be found.'
      );

      return;
    }

    /*
     * Prevent duplicate requests when the button is clicked repeatedly.
     */
    if (button.dataset.loading === 'true') {
      return;
    }

    const currentUrl = this.productPageUrl;

    if (!currentUrl) {
      console.warn(
        '[quick-view] Product URL could not be found.'
      );

      return;
    }

    /*
     * Save the exact original contents so the plus icon and label can be
     * restored after loading.
     */
    const originalButtonHtml = button.innerHTML;

    button.dataset.loading = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    button.innerHTML = `
      <span
        class="quick-view-loading-spinner"
        aria-hidden="true"
      ></span>

      <span class="quick-view-loading-label">
        Loading...
      </span>
    `;

    /*
     * Allow the browser to paint the loading state before beginning
     * the product-page request.
     */
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    try {
      if (
        this.dataset.usesSellingPlans === 'true'
      ) {
        window.location.href = currentUrl;
        return;
      }

      /*
       * Reuse cached content when this product was already opened.
       */
      let productGrid =
        this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        const html =
          await this.fetchProductPage(currentUrl);

        if (html) {
          const gridElement = html.querySelector(
  '[data-product-grid-content]'
);

if (gridElement) {
  /*
   * Clone Fabric's main product grid.
   */
  const clonedGrid =
    /** @type {Element} */ (
      gridElement.cloneNode(true)
    );

  /*
   * The additional product blocks are rendered outside
   * [data-product-grid-content], but inside the main
   * product-information section.
   */
  const productInformationSection =
    gridElement.closest(
      '[data-testid="product-information"]'
    );

  const clonedProductDetails =
    clonedGrid.querySelector(
      '.product-details'
    );

  if (
    productInformationSection &&
    clonedProductDetails
  ) {
    const sectionChildren = Array.from(
      productInformationSection.children
    );

    const gridPosition =
      sectionChildren.indexOf(gridElement);

    /*
     * Everything rendered after the product grid represents
     * {{ additional_blocks }} in product-information-content.
     */
    const additionalNodes =
      gridPosition >= 0
        ? sectionChildren.slice(gridPosition + 1)
        : [];

    if (additionalNodes.length > 0) {
      const additionalBlocksWrapper =
        document.createElement('div');

      additionalBlocksWrapper.classList.add(
        'quick-view-additional-blocks'
      );

      for (const node of additionalNodes) {
        /*
         * Do not copy scripts directly into the modal.
         * Theme and app scripts are already loaded globally.
         */
        if (
          node.tagName === 'SCRIPT' ||
          node.tagName === 'STYLE'
        ) {
          continue;
        }

        additionalBlocksWrapper.appendChild(
          node.cloneNode(true)
        );
      }

      /*
       * Put description, accordions and other additional blocks
       * inside the right-side product details area.
       */
      clonedProductDetails.appendChild(
        additionalBlocksWrapper
      );
    }
  }

  productGrid = clonedGrid;

  this.#cachedContent.set(
    currentUrl,
    productGrid
  );
}
        }
      }

      if (!productGrid) {
        throw new Error(
          'Quick View product content could not be found.'
        );
      }

      /*
       * Always insert a fresh clone so changes made during a previous
       * opening do not affect the cached copy.
       */
      const freshContent =
        /** @type {Element} */ (
          productGrid.cloneNode(true)
        );

      await this.updateQuickAddModal(
        freshContent
      );

      this.#updateVariantPicker(productGrid);

      this.#openQuickAddModal();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error(
          '[quick-view] Failed to open Quick View:',
          error
        );
      }
    } finally {
      /*
       * Restore the original Quick View button.
       */
      button.innerHTML = originalButtonHtml;
      button.dataset.loading = 'false';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * Resets the product media and details scroll positions.
   */
  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /**
   * Keeps the product-card Quick View button visible until the dialog closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute(
          'stay-visible',
          false
        );
      },
      {
        once: true,
      }
    );
  }

  /**
   * Opens the Fabric Quick Add modal.
   */
  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    /*
     * Fabric waits for the opening animation before resetting scroll.
     */
    const dialog = dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  /**
   * Closes Quick View when the responsive breakpoint changes.
   */
  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page.
   *
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    /*
     * Abort an earlier product request when another request begins.
     */
    this.#abortController?.abort();
    this.#abortController =
      new AbortController();

    try {
      const response = await fetch(
        productPageUrl,
        {
          signal:
            this.#abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      return new DOMParser().parseFromString(
        responseText,
        'text/html'
      );
    } catch (error) {
      if (error?.name === 'AbortError') {
        return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Inserts product content into the Quick View modal.
   *
   * @param {Element} productGrid
   */
  async updateQuickAddModal(productGrid) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!productGrid || !modalContent) {
      return;
    }

    /*
     * Fabric uses a compact product layout on mobile.
     */
    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    /*
     * Synchronize the standard product view event payload.
     */
    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(modalContent, productGrid);

    this.#syncVariantSelection(
      modalContent
    );
  }

  /**
   * Synchronizes the product-card variant with the Quick View modal.
   *
   * @param {Element} modalContent
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Closes the dialog after a successful cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates product links when the selected variant changes.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const anchorElement =
          /** @type {HTMLAnchorElement} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        if (!anchorElement) return;

        if (viewMoreDetailsLink) {
          viewMoreDetailsLink.href =
            anchorElement.href;
        }

        if (mobileProductTitle) {
          mobileProductTitle.href =
            anchorElement.href;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Fabric's iOS 16 dialog-close rendering workaround.
   */
  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid =
        /** @type {HTMLElement | null} */ (
          document.querySelector(
            '#ResultsList [product-grid-view]'
          )
        );

      if (!grid) return;

      const currentWidth =
        grid.getBoundingClientRect().width;

      grid.style.width =
        `${currentWidth - 1}px`;

      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}